/**
 * G-9 — Classification inputs that cannot be derived from current staff rows.
 *
 * Three award facts are keyed to a past classification date:
 *   1. Retained / red-circled rates (MA000018 Sch I, MA000100 Sch G, MA000034 Sch F)
 *   2. MA000018 B.2.4 direct-care L4 (4 years at L3 counted after 1 January 2025)
 *   3. MA000018 cl 28.3 five-week annual leave for ex-Nurses-Award nursing assistants
 *      covered on 31 December 2024
 *
 * Missing facts stay UNRESOLVED. This module does not invent a frozen dollar
 * floor, count pre-2025 service toward L4, or default leave to four weeks.
 */

export const AGED_CARE_DC_L4_SERVICE_COUNT_START = '2025-01-01';
export const AGED_CARE_DC_L4_EARLIEST_B24_DATE = '2029-01-01';
export const NURSES_AIN_LEAVE_SNAPSHOT_DATE = '2024-12-31';

export const RETAINED_RATE_SNAPSHOT_BY_AWARD = {
  MA000018: { schedule: 'I', classifiedOn: '2024-12-31' },
  MA000100: { schedule: 'G', classifiedOn: '2024-12-31' },
  MA000034: { schedule: 'F', classifiedOn: '2025-02-28' },
} as const;

export type RetainedRateAwardCode = keyof typeof RETAINED_RATE_SNAPSHOT_BY_AWARD;

export type ClassificationHistoricalKind =
  | 'RETAINED_RATE'
  | 'AGED_CARE_DC_L4_SERVICE'
  | 'EX_NURSES_AIN_FIVE_WEEK_LEAVE';

export type ClassificationHistoricalStatus = 'RESOLVED' | 'UNRESOLVED';

export type AgedCareDcL4Path = 'TRANSLATED_I11' | 'B24_SERVICE';

export type ClassificationHistoricalCode =
  | 'RETAINED_RATE_CLASSIFIED_ON_SNAPSHOT_UNRESOLVED'
  | 'RETAINED_RATE_CLASSIFIED_ON_SNAPSHOT'
  | 'AGED_CARE_DC_L4_PATH_UNRESOLVED'
  | 'AGED_CARE_DC_L4_B24_UNREACHABLE'
  | 'AGED_CARE_DC_L4_PRE_2025_SERVICE_COUNTED'
  | 'AGED_CARE_DC_L4_TRANSLATED_I11'
  | 'AGED_CARE_DC_L4_B24_SERVICE'
  | 'EX_NURSES_AIN_LEAVE_UNRESOLVED'
  | 'EX_NURSES_AIN_LEAVE_GRANDFATHERED'
  | 'EX_NURSES_AIN_LEAVE_NOT_IN_SCOPE';

export interface ClassificationHistoricalResult {
  kind: ClassificationHistoricalKind;
  status: ClassificationHistoricalStatus;
  code: ClassificationHistoricalCode;
  message: string;
}

export interface ClassificationHistoricalInput {
  awardCode?: string | null;
  classificationStream?: string | null;
  classificationLevel?: string | null;
  /** Tenant-local YYYY-MM-DD being assessed. */
  asOfDate?: string | null;
  /**
   * Whether the worker was classified on the award translation snapshot date.
   * Null/undefined = unknown — do not assume they were or were not.
   */
  classifiedOnTranslationSnapshot?: boolean | null;
  /**
   * How a MA000018 direct-care L4 classification was reached.
   * TRANSLATED_I11 = Schedule I.1.1 (old L5 without Cert IV).
   * B24_SERVICE = Schedule B.2.4 (4 years at L3 after 1 January 2025).
   * Null/undefined = unknown — do not assume either path.
   */
  agedCareDcL4Path?: AgedCareDcL4Path | null;
  /**
   * Inclusive start of the L3 classification used to count B.2.4 years.
   * Pre-2025-01-01 dates over-classify.
   */
  agedCareL3ClassifiedFrom?: string | null;
  /**
   * Nursing assistant covered by MA000034 on 31 December 2024 who moved
   * under PR779152. Null/undefined = unknown — do not default leave weeks.
   */
  nursesAwardNursingAssistantOn20241231?: boolean | null;
}

function normaliseAward(awardCode: string | null | undefined): string {
  return (awardCode ?? '').trim().toUpperCase();
}

function isDirectCareStream(stream: string | null | undefined): boolean {
  const value = (stream ?? '').trim().toLowerCase();
  return value === 'direct_care' || value === 'direct-care' || value === 'direct care';
}

function isAgedCareDcL4(level: string | null | undefined): boolean {
  const value = (level ?? '').trim().toUpperCase();
  return (
    value === 'L4' || value === 'DC4' || value === '4' || value === 'LEVEL_4' || value === 'LEVEL 4'
  );
}

function isRetainedRateAward(awardCode: string): awardCode is RetainedRateAwardCode {
  return awardCode in RETAINED_RATE_SNAPSHOT_BY_AWARD;
}

function resolveRetainedRate(
  input: ClassificationHistoricalInput,
): ClassificationHistoricalResult | null {
  const awardCode = normaliseAward(input.awardCode);
  if (!isRetainedRateAward(awardCode)) return null;
  const snapshot = RETAINED_RATE_SNAPSHOT_BY_AWARD[awardCode];
  if (input.classifiedOnTranslationSnapshot == null) {
    return {
      kind: 'RETAINED_RATE',
      status: 'UNRESOLVED',
      code: 'RETAINED_RATE_CLASSIFIED_ON_SNAPSHOT_UNRESOLVED',
      message: `${awardCode} Schedule ${snapshot.schedule} retained rate needs whether the worker was classified on ${snapshot.classifiedOn}. That date cannot be derived from the current classification.`,
    };
  }
  return {
    kind: 'RETAINED_RATE',
    status: 'RESOLVED',
    code: 'RETAINED_RATE_CLASSIFIED_ON_SNAPSHOT',
    message: input.classifiedOnTranslationSnapshot
      ? `${awardCode} Schedule ${snapshot.schedule} classified-on-${snapshot.classifiedOn} fact is present. Apply the higher of the translated rate and the snapshot rate; do not invent the dollar floor here.`
      : `${awardCode} Schedule ${snapshot.schedule} does not apply — worker was not classified on ${snapshot.classifiedOn}.`,
  };
}

function resolveAgedCareDcL4(
  input: ClassificationHistoricalInput,
): ClassificationHistoricalResult | null {
  if (normaliseAward(input.awardCode) !== 'MA000018') return null;
  if (
    !isDirectCareStream(input.classificationStream) ||
    !isAgedCareDcL4(input.classificationLevel)
  ) {
    return null;
  }

  if (input.agedCareDcL4Path === 'TRANSLATED_I11') {
    return {
      kind: 'AGED_CARE_DC_L4_SERVICE',
      status: 'RESOLVED',
      code: 'AGED_CARE_DC_L4_TRANSLATED_I11',
      message:
        'Direct-care L4 is reached via Schedule I.1.1 (previous L5 without relevant Certificate IV). B.2.4 service years are not required.',
    };
  }

  if (input.agedCareDcL4Path === 'B24_SERVICE') {
    const l3From = input.agedCareL3ClassifiedFrom?.trim() ?? '';
    if (l3From && l3From < AGED_CARE_DC_L4_SERVICE_COUNT_START) {
      return {
        kind: 'AGED_CARE_DC_L4_SERVICE',
        status: 'UNRESOLVED',
        code: 'AGED_CARE_DC_L4_PRE_2025_SERVICE_COUNTED',
        message:
          'B.2.4 counts four years classified at level 3 after 1 January 2025. Pre-2025 service must not be counted.',
      };
    }
    const asOf = input.asOfDate?.trim() ?? '';
    if (!asOf || asOf < AGED_CARE_DC_L4_EARLIEST_B24_DATE) {
      return {
        kind: 'AGED_CARE_DC_L4_SERVICE',
        status: 'UNRESOLVED',
        code: 'AGED_CARE_DC_L4_B24_UNREACHABLE',
        message:
          'B.2.4 L4 needs four years at L3 after 1 January 2025, so it is unreachable before 1 January 2029.',
      };
    }
    return {
      kind: 'AGED_CARE_DC_L4_SERVICE',
      status: 'RESOLVED',
      code: 'AGED_CARE_DC_L4_B24_SERVICE',
      message:
        'B.2.4 L4 path is dated on or after 1 January 2029 and does not count pre-2025 L3 service.',
    };
  }

  return {
    kind: 'AGED_CARE_DC_L4_SERVICE',
    status: 'UNRESOLVED',
    code: 'AGED_CARE_DC_L4_PATH_UNRESOLVED',
    message:
      'Direct-care L4 is either Schedule I.1.1 translation or B.2.4 service after 1 January 2025. Current staff rows cannot tell which. Do not assume either path.',
  };
}

function resolveExNursesAinLeave(
  input: ClassificationHistoricalInput,
): ClassificationHistoricalResult | null {
  if (normaliseAward(input.awardCode) !== 'MA000018') return null;
  if (input.nursesAwardNursingAssistantOn20241231 == null) {
    return {
      kind: 'EX_NURSES_AIN_FIVE_WEEK_LEAVE',
      status: 'UNRESOLVED',
      code: 'EX_NURSES_AIN_LEAVE_UNRESOLVED',
      message:
        'cl 28.3 five-week leave needs whether this worker was a Nurses Award nursing assistant on 31 December 2024. Do not default to four weeks.',
    };
  }
  if (input.nursesAwardNursingAssistantOn20241231) {
    return {
      kind: 'EX_NURSES_AIN_FIVE_WEEK_LEAVE',
      status: 'RESOLVED',
      code: 'EX_NURSES_AIN_LEAVE_GRANDFATHERED',
      message:
        'cl 28.3 applies — same annual-leave quantum as MA000034 (NES plus one additional week).',
    };
  }
  return {
    kind: 'EX_NURSES_AIN_FIVE_WEEK_LEAVE',
    status: 'RESOLVED',
    code: 'EX_NURSES_AIN_LEAVE_NOT_IN_SCOPE',
    message: 'cl 28.3 does not apply. Leave follows cl 28.2 / NES, not a silent four-week default.',
  };
}

export function resolveClassificationHistoricalInputs(
  input: ClassificationHistoricalInput,
): ClassificationHistoricalResult[] {
  return [
    resolveRetainedRate(input),
    resolveAgedCareDcL4(input),
    resolveExNursesAinLeave(input),
  ].filter((result): result is ClassificationHistoricalResult => result !== null);
}

export function unresolvedClassificationHistoricalInputs(
  input: ClassificationHistoricalInput,
): ClassificationHistoricalResult[] {
  return resolveClassificationHistoricalInputs(input).filter(
    (result) => result.status === 'UNRESOLVED',
  );
}
